import cx from 'classnames';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';

import './DetailsToggle.sass';

export const DetailsToggle = ({
  dataQaId,
  children,
  defaultOpen = false,
  wrapperClassName = null,
  contentClassName = null,
  autoFocusListener = null,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(defaultOpen);
  const [moreDetailsHeight, setMoreDetailsHeight] = useState(0);
  const isDesktop = useIsLargishAndUp();

  const moreDetailsRef = useRef(null);

  const updateHeight = () => {
    // Add 1 to account for bottom border of link preview
    setMoreDetailsHeight(`${moreDetailsRef.current.offsetHeight + 1}px`);
  };

  useEffect(() => {
    setDetailsOpen((state) => {
      if (state) return state; // Do not automatically close the accordion if it's already been opened
      return defaultOpen;
    });
  }, [defaultOpen]);

  useEffect(() => {
    if (moreDetailsRef.current) {
      const observer = new ResizeObserver(updateHeight);
      observer.observe(moreDetailsRef.current);
      return () => observer.disconnect();
    }
    return () => {};
  }, [moreDetailsRef?.current]);

  const onToggle = useCallback(() => {
    setDetailsOpen((o) => !o);
    autoFocusListener?.();
  }, []);

  return (
    <div className={cx('builder__details-toggle', wrapperClassName)}>
      <Button
        className="builder__details-toggle__button"
        data-qa-id={dataQaId}
        onClick={onToggle}
        variant="unstyled-button"
      >
        <Typography variant={isDesktop ? 'paragraph2' : 'paragraph3'} semibold color="asphalt">
          {detailsOpen ? 'Hide' : 'Add'} additional details
        </Typography>
        <ChevronIcon ratio={0.5} direction={detailsOpen ? 'up' : 'down'} />
      </Button>
      <div
        className={cx('builder__details-toggle__more-details', {open: !!detailsOpen})}
        style={{height: detailsOpen ? moreDetailsHeight : 0}}
      >
        <div
          className={cx('builder__details-toggle__more-details-content', contentClassName)}
          ref={moreDetailsRef}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
